import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Header, Spinner, Alert } from '@cloudscape-design/components';
import { useAuth } from '../contexts/AuthContext';

const Callback: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuth2Callback } = useAuth();

  // Use a ref to track processing state across renders
  const processingRef = useRef(false);
  
  useEffect(() => {
    console.log('Callback useEffect running at:', new Date().toISOString());
    
    const processCallback = async () => {
      // Prevent multiple processing attempts using ref
      if (processingRef.current) {
        console.log('Already processing OAuth callback, skipping duplicate execution');
        return;
      }
      processingRef.current = true;
      
      try {
        // Get the authorization code from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Log the full URL and all parameters for debugging
        console.log('Callback URL:', window.location.href);
        console.log('Search params:', window.location.search);
        
        // Log all URL parameters
        console.log('All URL parameters:', Array.from(urlParams.entries()));
        console.log('Code present:', !!code);
        console.log('Error present:', !!error);
        
        // Check if this code has already been processed
        const processedCodes = JSON.parse(sessionStorage.getItem('processed_auth_codes') || '[]');
        if (code && processedCodes.includes(code)) {
          console.warn('Authorization code has already been processed, preventing reuse');
          setError('Authentication session expired or already used');
          setIsProcessing(false);
          
          navigate('/login', { 
            state: { error: 'Authentication session expired or already used' },
            replace: true
          });
          return;
        }
        
        // Clear the URL to prevent code reuse if page is refreshed
        if (code && window.history.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          console.log('URL cleaned to prevent code reuse');
        }
        
        // Add this code to the processed list if it exists
        if (code) {
          processedCodes.push(code);
          sessionStorage.setItem('processed_auth_codes', JSON.stringify(processedCodes));
        }

        // Check for OAuth2 errors
        if (error) {
          console.error('OAuth2 error:', error, errorDescription);
          setError(`Authentication failed: ${errorDescription || error}`);
          setIsProcessing(false);
          
          // Redirect immediately without timeout
          navigate('/login', { 
            state: { 
              error: `Authentication failed: ${errorDescription || error}` 
            },
            replace: true // Use replace to prevent back navigation to the error state
          });
          return;
        }

        // Check if we have an authorization code
        if (!code) {
          console.error('No authorization code received. Full URL:', window.location.href);
          setError('No authorization code received. This could be due to a session timeout or browser privacy settings.');
          setIsProcessing(false);
          
          // Redirect immediately without timeout
          navigate('/login', { 
            state: { 
              error: 'Authentication failed: No authorization code received. This could be due to a session timeout or browser privacy settings.'
            },
            replace: true // Use replace to prevent back navigation to the error state
          });
          return;
        }

        // Process the OAuth2 callback
        await handleOAuth2Callback(code);

        // Get the intended destination from sessionStorage or default to dashboard
        const storedRedirect = sessionStorage.getItem('oauth2_redirect_after_login');
        const from = storedRedirect || location.state?.from?.pathname || '/dashboard';
        
        // Clear the stored redirect
        sessionStorage.removeItem('oauth2_redirect_after_login');
        
        // Redirect to the intended destination
        navigate(from, { replace: true });

      } catch (error: any) {
        console.error('Error processing OAuth2 callback:', error);
        setError(error.message || 'Authentication failed');
        setIsProcessing(false);
        
        // Redirect immediately without timeout
        navigate('/login', { 
          state: { 
            error: error.message || 'Authentication failed'
          },
          replace: true // Use replace to prevent back navigation to the error state
        });
      } finally {
        // Reset processing flag in finally block to ensure it's reset even if there's an error
        processingRef.current = false;
      }
    };

    processCallback();
  }, [handleOAuth2Callback, navigate, location.state]);

  if (error) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Header variant="h2">Authentication Error</Header>
          <Alert type="error" header="Authentication Failed">
            {error}
          </Alert>
          <p style={{ marginTop: '1rem', color: '#666' }}>
            Redirecting to login page...
          </p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Header variant="h2">Processing Authentication</Header>
        <div style={{ margin: '2rem 0' }}>
          <Spinner size="large" />
        </div>
        <p style={{ color: '#666' }}>
          Please wait while we complete your authentication...
        </p>
      </div>
    </Container>
  );
};

export default Callback;
